/*
 * The one add-to-cart path for cards, quick view and the product page:
 * auth gate, store add (which enforces the per-order limit), the cover
 * flying to the header cart, and a toast — "Added · View cart" or the
 * limit message. `buyNow()` is the direct checkout hand-off; `/checkout`
 * is auth-guarded, so anonymous users land on login with `?buy=` kept.
 */
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '../stores/auth';
import { KINGUIN_ORDER_LIMIT, useCartStore, type CartItem } from '../stores/cart';
import { useToastStore } from '../stores/toast';
import { isPurchasable, productCover, type ProductSummary } from '../types/product';
import { flyToCart } from './useFlyToCart';

export interface AddToCartOptions {
  /** Element the cover flies from (the button or the card cover). */
  from?: Element | null;
  /** A specific seller offer; without one the server picks the cheapest. */
  offer?: { id: string; merchantName: string | null; ourPriceCents: number; qty: number };
  qty?: number;
}

export function useAddToCart() {
  const auth = useAuthStore();
  const cart = useCartStore();
  const toast = useToastStore();
  const router = useRouter();
  const route = useRoute();
  const { t } = useI18n();

  function requireAuth(): boolean {
    if (auth.isAuthenticated) return true;
    void router.push({ name: 'login', query: { next: route.fullPath } });
    return false;
  }

  /** Returns true when a line was added. */
  function add(product: ProductSummary, options: AddToCartOptions = {}): boolean {
    if (!requireAuth()) return false;
    // A specific offer row is live by the caller's own check; only the
    // offer-less button relies on the catalog snapshot.
    if (!options.offer && !isPurchasable(product)) return false;
    const line: Omit<CartItem, 'qty'> = {
      productId: product.id,
      offerId: options.offer?.id,
      merchantName: options.offer?.merchantName ?? undefined,
      name: product.name,
      priceCents: options.offer?.ourPriceCents ?? product.ourPriceCents,
      coverUrl: product.coverUrl,
      imageUrl: product.imageUrl,
      platform: product.platform,
      maxQty: options.offer?.qty ?? product.totalQty,
    };
    const ok = cart.add(line, options.qty ?? 1);
    const image = productCover(product);
    if (ok) {
      flyToCart(options.from, image);
      toast.push({ title: t('common.toast.addedToCart'), body: product.name, image, action: { label: t('common.toast.viewCart'), to: '/cart' } });
    } else {
      toast.push({ title: t('common.toast.cartLimit', { limit: KINGUIN_ORDER_LIMIT }), tone: 'error', action: { label: t('common.toast.viewCart'), to: '/cart' } });
    }
    return ok;
  }

  function buyNow(product: Pick<ProductSummary, 'id' | 'available' | 'offerCount' | 'totalQty'>) {
    if (!isPurchasable(product)) return;
    void router.push({ name: 'checkout', query: { buy: product.id } });
  }

  return { add, buyNow };
}
